import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { breakApartOmnisearch } from "utils/omnisearchUtils";

import { CredentialFacilityRow } from "./CredentialFacilitiesGrid";

/**
 * Keys whose values are ids or codes rather than prose, so a term matches the whole
 * value. The rest are substring matches, which is what a typed fragment should do.
 */
const EXACT_MATCH_KEYS = new Set(["facility", "facilityId", "organization", "state"]);

function addressOf(row: CredentialFacilityRow): string {
  return [row.streetAddress1, row.city, row.state, row.zip].filter(Boolean).join(", ");
}

/** Keyed by filter key, so a `key:value` term reads the field its header names. */
function keyedFields(row: CredentialFacilityRow): Record<string, string> {
  return {
    // Ids, not names: the popover writes `key:a|b` unquoted, so a value containing a
    // space would tokenize apart. This is the same shape the server-side omnisearch uses
    // for `facilityId:`/`tenantId:`.
    facilityId: row.id,
    // `FilterPopover` canonicalises `facility` to `facilityId`, but a hand-typed search
    // string arrives here uncanonicalised.
    facility: row.id,
    organization: row.organizationId ?? "",
    state: row.state ?? "",
    streetAddress: addressOf(row),
    lepc: row.lepcName ?? "",
  };
}

/** What free text searches: the names a user can see, never the ids behind them. */
function freeTextOf(row: CredentialFacilityRow): string {
  return [row.name, row.organizationName, addressOf(row), row.state, row.lepcName]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

const unquote = (value: string) => value.replace(/^["']|["']$/g, "");

function distinct<T extends string>(values: (T | null | undefined)[]): T[] {
  return [...new Set(values.filter((value): value is T => !!value))].sort((a, b) =>
    a.localeCompare(b)
  );
}

/**
 * Filter options come from the credential's own facilities, not from a global picker.
 * Left to their defaults, the facility and state keys resolve to the app-wide facility
 * and state pickers, which offer thousands of rows this credential has nothing to do with.
 */
export function facilityFilterKeys(
  rows: CredentialFacilityRow[],
  showOrganization: boolean
): FilterKey[] {
  const facilityNames = new Map(rows.map((row) => [row.id, row.name]));
  const organizationNames = new Map(
    rows.flatMap((row) => (row.organizationId ? [[row.organizationId, row.organizationName]] : []))
  );

  const organizationKey: FilterKey = {
    key: "organization",
    header: "Organization",
    filterKeyType: "enumAutocomplete",
    enumValues: distinct(rows.map((row) => row.organizationId)),
    // Verbatim: these are real names, and the default pretty-printer title-cases them.
    enumPresentationFunction: (value) => organizationNames.get(value) ?? value,
  };

  return [
    ...(showOrganization ? [organizationKey] : []),
    {
      // `facilityId`, not `facility`: `FilterPopover` canonicalises the latter to the former
      // on read and writes the canonical form back, so a `facility` key loses its selection
      // when the popover reopens and then emits a term this filter doesn't answer to.
      key: "facilityId",
      header: "Facility",
      filterKeyType: "enumAutocomplete",
      enumValues: distinct(rows.map((row) => row.id)),
      enumPresentationFunction: (value) => facilityNames.get(value) ?? value,
    },
    { key: "streetAddress", header: "Street Address" },
    {
      key: "state",
      header: "State/Territory",
      filterKeyType: "enumAutocomplete",
      enumValues: distinct(rows.map((row) => row.state)),
      enumPresentationFunction: (value) => value,
    },
    { key: "lepc", header: "LEPC" },
  ];
}

/**
 * Applies an omnisearch string to a row in memory. The whole linked set arrives with the
 * credential, so this filters client-side rather than going back to the server the way a
 * page-level omnisearch grid would — which also keeps unsaved staged rows visible.
 */
export function matchesOmnisearch(row: CredentialFacilityRow, search: string): boolean {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(search);
  const fields = keyedFields(row);

  const haystack = freeTextOf(row);
  const words = nonKvpValues.split(/\s+/).map(unquote).filter(Boolean);
  if (!words.every((word) => haystack.includes(word.toLowerCase()))) return false;

  for (const [key, value] of kvpValues) {
    const field = fields[key];
    // An unrecognised key is ignored rather than read as "matches nothing", which is how
    // the server-side omnisearch treats a key it has no handler for.
    if (field === undefined) continue;

    // `a|b` is the any-of form the filter popover produces for multi-value keys.
    const wanted = value.split("|").map(unquote).filter(Boolean);
    if (!wanted.length) continue;

    const matched = EXACT_MATCH_KEYS.has(key)
      ? wanted.some((v) => v.toLowerCase() === field.toLowerCase())
      : wanted.some((v) => field.toLowerCase().includes(v.toLowerCase()));
    if (!matched) return false;
  }

  return true;
}

export function filterFacilityRows(
  rows: CredentialFacilityRow[],
  search: string
): CredentialFacilityRow[] {
  if (!search.trim()) return rows;
  return rows.filter((row) => matchesOmnisearch(row, search));
}
